import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ClassesEntity } from '../../../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../../../injection-tokens/entity-service-tokens';
import { ExportService } from '../../../../../../../../services/export.service';
import { ConfirmDialogService } from '../../../../../../../../services/confirm-dialog.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../../../../../shared/pagination-options';

@Component({
  selector: 'app-user-accounts-sub-tab',
  templateUrl: './user-accounts-sub-tab.component.html',
  styleUrls: ['./user-accounts-sub-tab.component.css'],
})
export class UserAccountsSubTabComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() classId!: string;
  @Input() classEntity!: ClassesEntity;
  @Input() canDestroyResources = false;
  @Input() userAccounts: any[] = [];
  @Input() isLoading = false;
  @Input() loadError: string | null = null;
  @Input() exportService!: ExportService;

  @Output() refreshRequested = new EventEmitter<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  readonly displayedColumns = [
    'participant', 'inviteEmail', 'redeemEmail',
    'cloudUsername', 'cloudPassword', 'subscriptionId', 'description', 'actions',
  ];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  dataSource = new MatTableDataSource<any>([]);

  /** Set of account ids whose password is currently revealed. */
  revealedIds = new Set<string>();
  /** Tracks which account id has an in-flight action. */
  busyIds = new Set<string>();
  /** True while bulk destroy is running. */
  isBulkBusy = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userAccounts']) {
      this.dataSource.data = this.userAccounts ?? [];
      // Clear revealed passwords when data reloads
      this.revealedIds.clear();
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  isRevealed(acc: any): boolean {
    return this.revealedIds.has(acc.userId ?? acc.id);
  }

  toggleReveal(acc: any): void {
    const key = acc.userId ?? acc.id;
    if (this.revealedIds.has(key)) {
      this.revealedIds.delete(key);
    } else {
      this.revealedIds.add(key);
    }
  }

  isBusy(acc: any): boolean {
    return this.busyIds.has(acc.userId ?? acc.id);
  }

  truncate(val: string | null | undefined, len = 20): string {
    if (!val) return '—';
    return val.length > len ? val.slice(0, len) + '…' : val;
  }

  private todayStr(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private notify(msg: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.snackBar.open(msg, 'Dismiss', {
      duration: 4000,
      panelClass: type === 'error' ? ['snack-error'] : type === 'info' ? ['snack-info'] : [],
    });
  }

  // ---------------------------------------------------------------------------
  // CSV Export — passwords always [REDACTED]
  // ---------------------------------------------------------------------------

  exportCsv(): void {
    const rows = this.dataSource.data.map(acc => ({
      'Participant': acc.participant ?? '',
      'Invite Email': acc.inviteEmail ?? '',
      'Redeem Email': acc.redeemEmail ?? '',
      'Cloud Username': acc.cloudUsername ?? '',
      'Password': '[REDACTED]',
      'Subscription': acc.subscriptionId ?? '',
      'Description': acc.description ?? '',
    }));
    this.exportService.toCsv(rows, `${this.classId}_user_accounts_${this.todayStr()}`);
  }

  // ---------------------------------------------------------------------------
  // Per-row destroy (gated by canDestroyResources)
  // ---------------------------------------------------------------------------

  destroyAccount(acc: any): void {
    this.confirmDialog.typedConfirm({
      title: `Destroy Account — ${acc.participant ?? acc.cloudUsername}`,
      message: `Permanently destroys the cloud account "${acc.cloudUsername}" and all associated Azure AD resources. ` +
               `This action cannot be undone.`,
      details: [
        `Participant: ${acc.participant ?? '—'}`,
        `Cloud Username: ${acc.cloudUsername ?? '—'}`,
        `Subscription: ${acc.subscriptionId ?? '—'}`,
      ],
      type: 'error',
      confirmPhrase: 'DESTROY',
      confirmButtonText: 'Destroy Account',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      const key = acc.userId ?? acc.id;
      this.busyIds.add(key);
      this.classService.destroyUserAccount(acc.userId, this.classId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.busyIds.delete(key); this.notify(`Account "${acc.cloudUsername}" destroyed.`); this.refreshRequested.emit(); },
          error: () => { this.busyIds.delete(key); this.notify(`Failed to destroy account "${acc.cloudUsername}".`, 'error'); },
        });
    });
  }

  // ---------------------------------------------------------------------------
  // Bulk destroy all accounts (gated)
  // ---------------------------------------------------------------------------

  destroyAllAccounts(): void {
    const count = this.dataSource.data.length;
    this.confirmDialog.typedConfirm({
      title: `Destroy All Accounts`,
      message: `Permanently destroys all ${count} user account(s) and their associated Azure AD resources. ` +
               `This action cannot be undone.`,
      type: 'error',
      confirmPhrase: 'DESTROY ALL',
      confirmButtonText: 'Destroy All Accounts',
    }).pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (!result.confirmed) return;
      this.isBulkBusy = true;
      // Backend accepts 'all' as userId to destroy every account in the class
      this.classService.destroyUserAccount('all', this.classId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => { this.isBulkBusy = false; this.notify(`All ${count} account(s) destroyed.`); this.refreshRequested.emit(); },
          error: () => { this.isBulkBusy = false; this.notify('Failed to destroy all accounts.', 'error'); },
        });
    });
  }
}
